import React, { useState, useEffect } from 'react';
import { COLORS } from '../../Constants/theme';

/**
 * A modern form field component that follows the Unisoft brand design
 */
const FormField = ({
  label,
  type = 'text',
  name,
  value,
  onChange,
  placeholder,
  error,
  touched,
  disabled = false,
  required = false,
  className = '',
  icon = null,
  onIconClick = null,
  helpText = '',
  as = 'input',
  options = [],
  rows = 4,
  legend = false
}) => {
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (value) {
      setIsFocused(true);
    } else {
      setIsFocused(false);
    }
  }, [value]);

  const handlePlaceholderClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const input = document.getElementById(name);
    if (input && !disabled) {
      input.focus();
      setIsFocused(true);
    }
  };

  const getInputClasses = () => {
    let baseClasses = `
      w-full
      px-3
      h-[46px]
      rounded-lg
      bg-white
      text-gray-800
      text-left
      placeholder-gray-400
      transition-all
      duration-200
      focus:outline-none
      ${disabled ? 'bg-gray-50 cursor-not-allowed opacity-70' : ''}
      ${className}
    `;

    if (legend) {
      return `${baseClasses} mt-[2px]`;
    }

    return baseClasses;
  };

  const getLabelClasses = () => {
    let baseClasses = `absolute left-3 transition-all z-10 bg-white px-1 text-left`;
    
    if (isFocused || value) {
      if (error && touched) {
        return `${baseClasses} -top-[0.6rem] text-sm text-gray-700`;
      } else if (isFocused) {
        return `${baseClasses} -top-[0.6rem] text-sm text-gray-700`;
      } else {
        return `${baseClasses} -top-[0.6rem] text-sm text-gray-700`;
      }
    } else {
      return `${baseClasses} top-1/2 text-gray-500 text-sm -translate-y-1/2`;
    }
  };

  const getContainerClasses = () => {
    let baseClasses = "relative w-full transition-all duration-200";
    
    if (error && touched) {
      return `${baseClasses} border-2 border-indigo-300 rounded-lg`;
    } else if (isFocused) {
      return `${baseClasses} border-2 border-indigo-500 rounded-lg`;
    } else {
      return `${baseClasses} border border-gray-300 hover:border-indigo-500 rounded-lg ${disabled ? 'border-gray-200' : ''}`;
    }
  };

  const renderField = () => {
    const commonProps = {
      id: name,
      name: name,
      value: value,
      onChange: onChange,
      placeholder: legend ? '' : placeholder,
      disabled: disabled,
      className: getInputClasses(),
      onFocus: () => setIsFocused(true),
      onBlur: () => {
        if (!value) setIsFocused(false);
      }
    };

    switch (as) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={rows}
            className={`${getInputClasses()} min-h-[46px]`} // Ensure textarea doesn't shrink below 46px
          />
        );
      case 'select':
        return (
          <select {...commonProps}>
            <option value="">{legend ? '' : (placeholder || 'Select an option')}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      default:
        return (
          <div className="relative">
            <input
              {...commonProps}
              type={type}
            />
            {icon && (
              <div 
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 ${onIconClick ? 'cursor-pointer hover:text-gray-700' : ''}`}
                onClick={onIconClick}
              >
                {icon}
              </div>
            )}
          </div>
        );
    }
  };

  if (legend) {
    return (
      <div className="mb-4 relative w-full">
        <div className={getContainerClasses()}>
          <div 
            className={`${getLabelClasses()} cursor-pointer`}
            onClick={handlePlaceholderClick}
          >
            {label || placeholder}
            {required && <span className="text-rose-500 ml-1">*</span>}
          </div>
          {renderField()}
        </div>
        {helpText && (
          <p className="mt-1 text-sm text-gray-500">{helpText}</p>
        )}
      </div>
    );
  }

  return (
    <div className="mb-4 relative w-full">
      {label && (
        <label 
          htmlFor={name} 
          className="block text-sm font-medium mb-1 text-left text-gray-900 cursor-pointer"
        >
          {label}
          {required && <span className="text-rose-500 ml-1">*</span>}
        </label>
      )}
      <div className={getContainerClasses()}>
        {!label && placeholder && (
          <div 
            className={`${getLabelClasses()} cursor-pointer`}
            onClick={handlePlaceholderClick}
          >
            {placeholder}
            {required && <span className="text-rose-500 ml-1">*</span>}
          </div>
        )}
        {renderField()}
      </div>
      {helpText && (
        <p className="mt-1 text-xs text-gray-500">{helpText}</p>
      )}
      {error && touched && (
        <p className="mt-1 text-sm text-rose-500">{error}</p>
      )}
    </div>
  );
};

export default FormField;